'use client';
import React from 'react';
import Container from '@repo/ui/src/components/Container';
import { classNames } from '@repo/ui/src/utils/classNames';

interface PDPStickyNavProps {
  name?: string;
  size?: string;
  className?: string;
  children?: React.ReactNode;
}

const PDPStickyNav = ({ name, size, className, children }: PDPStickyNavProps) => {
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    let intersectionObserver: IntersectionObserver | null = null;
    let mutationObserver: MutationObserver | null = null;

    const setupIntersectionObserver = (targetElement: Element) => {
      intersectionObserver = new IntersectionObserver(
        ([entry]) => {
          const hasPassed = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
          setIsVisible(hasPassed);
        },
        { threshold: 0, rootMargin: '0px 0px 0px 0px' },
      );
      intersectionObserver.observe(targetElement);
    };

    const existing = document.querySelector('[data-product-cta]');
    if (existing) {
      setupIntersectionObserver(existing);
    } else {
      mutationObserver = new MutationObserver(() => {
        const element = document.querySelector('[data-product-cta]');
        if (element) {
          setupIntersectionObserver(element);
          mutationObserver?.disconnect();
        }
      });

      mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    return () => {
      intersectionObserver?.disconnect();
      mutationObserver?.disconnect();
    };
  }, []);

  return (
    <div
      className={classNames(
        'fixed top-[var(--navTop)] left-0 right-0 bg-neutral-600 z-[200] overflow-x-hidden',
        'transition-transform duration-300 ease-in-out',
        isVisible ? 'translate-y-0' : '-translate-y-full pointer-events-none',
        className,
      )}
      role="region"
      aria-label="product navigation"
      aria-hidden={!isVisible}
      inert={!isVisible}
    >
      <Container className="sm:grid sm:grid-cols-[minmax(0,1fr)_auto] items-center">
        {(name || size) && (
          <h2 className="tra-text-web-heading-7 text-warm-100 hidden sm:block">
            {name} {size && <span className="text-brand-300">{size}</span>}
          </h2>
        )}
        <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center">{children}</div>
      </Container>
    </div>
  );
};

export default PDPStickyNav;
